/**
 * Smart Packing Page
 * AI-suggested packing list based on trip details
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Check, Plus, Trash2, Briefcase, Sun, Cloud, Umbrella, Snowflake, Sparkles, Save, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import offlineStorage from '../services/offlineStorage';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: 'spring', stiffness: 260, damping: 20 }
  }
};

// Smart packing suggestions based on trip type
const PACKING_TEMPLATES = {
  essentials: {
    label: 'Essentials',
    emoji: '🎒',
    items: [
      { name: 'Passport & ID', checked: false },
      { name: 'Phone & charger', checked: false },
      { name: 'Cash & cards', checked: false },
      { name: 'Travel insurance docs', checked: false },
      { name: 'Copies of bookings', checked: false }
    ]
  },
  clothing: {
    label: 'Clothing',
    emoji: '👕',
    items: [
      { name: 'T-shirts (3-4)', checked: false },
      { name: 'Pants/shorts (2-3)', checked: false },
      { name: 'Underwear (5+)', checked: false },
      { name: 'Socks (5+ pairs)', checked: false },
      { name: 'Sleepwear', checked: false },
      { name: 'Light jacket', checked: false }
    ]
  },
  toiletries: {
    label: 'Toiletries',
    emoji: '🧴',
    items: [
      { name: 'Toothbrush & paste', checked: false },
      { name: 'Shampoo (travel size)', checked: false },
      { name: 'Sunscreen', checked: false },
      { name: 'Deodorant', checked: false },
      { name: 'Medications', checked: false }
    ]
  },
  tech: {
    label: 'Tech & Gadgets',
    emoji: '📱',
    items: [
      { name: 'Power bank', checked: false },
      { name: 'Universal adapter', checked: false },
      { name: 'Headphones', checked: false },
      { name: 'Camera (optional)', checked: false }
    ]
  }
};

// Weather-based additions
const WEATHER_ITEMS = {
  hot: [
    { name: 'Sunglasses', checked: false },
    { name: 'Sun hat', checked: false },
    { name: 'Swimwear', checked: false },
    { name: 'Flip flops', checked: false },
    { name: 'Aloe vera gel', checked: false }
  ],
  cold: [
    { name: 'Warm coat', checked: false },
    { name: 'Gloves', checked: false },
    { name: 'Beanie/hat', checked: false },
    { name: 'Thermal layers', checked: false },
    { name: 'Warm socks', checked: false }
  ],
  rainy: [
    { name: 'Rain jacket', checked: false },
    { name: 'Umbrella', checked: false },
    { name: 'Waterproof bag', checked: false },
    { name: 'Quick-dry clothes', checked: false }
  ]
};

// Vibe-based additions
const VIBE_ITEMS = {
  Beach: [
    { name: 'Beach towel', checked: false },
    { name: 'Reef-safe sunscreen', checked: false },
    { name: 'Snorkel gear', checked: false }
  ],
  Culture: [
    { name: 'Modest clothing', checked: false },
    { name: 'Guidebook', checked: false },
    { name: 'Notebook', checked: false }
  ],
  Party: [
    { name: 'Going-out outfit', checked: false },
    { name: 'Comfortable shoes', checked: false }
  ],
  Nature: [
    { name: 'Hiking shoes', checked: false },
    { name: 'Daypack', checked: false },
    { name: 'Bug spray', checked: false },
    { name: 'First aid kit', checked: false }
  ]
};

export default function SmartPackingPage({ trip, onBack }) {
  const [packingList, setPackingList] = useState({});
  const [selectedWeather, setSelectedWeather] = useState('hot');
  const [newItemText, setNewItemText] = useState('');
  const [addingToCategory, setAddingToCategory] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load saved packing list or generate new one
  useEffect(() => {
    loadPackingList();
  }, [trip]);

  const loadPackingList = async () => {
    setLoading(true);
    
    if (trip?.id) {
      const saved = await offlineStorage.getPackingList(trip.id);
      if (saved?.categories) {
        setPackingList(saved.categories);
        setSelectedWeather(saved.weather || 'hot');
        setLoading(false);
        return;
      }
    }
    
    // Generate new list
    generatePackingList();
    setLoading(false);
  };

  const generatePackingList = () => {
    const categories = { ...PACKING_TEMPLATES };
    
    // Add weather items
    categories.weather = {
      label: getWeatherLabel(selectedWeather),
      emoji: getWeatherEmoji(selectedWeather),
      items: [...WEATHER_ITEMS[selectedWeather]]
    };
    
    // Add vibe-specific items based on trip tags
    if (trip?.tags) {
      const vibeItems = [];
      trip.tags.forEach(tag => {
        const tagKey = Object.keys(VIBE_ITEMS).find(k => 
          tag.toLowerCase().includes(k.toLowerCase())
        );
        if (tagKey) {
          vibeItems.push(...VIBE_ITEMS[tagKey]);
        }
      });
      
      if (vibeItems.length > 0) {
        // Remove duplicates
        const uniqueItems = vibeItems.filter((item, index, self) =>
          index === self.findIndex(i => i.name === item.name)
        );
        categories.tripSpecific = {
          label: 'For This Trip',
          emoji: '✨',
          items: uniqueItems
        };
      }
    }
    
    setPackingList(categories);
    setHasChanges(false);
  };

  const getWeatherLabel = (weather) => {
    const labels = { hot: 'Hot Weather', cold: 'Cold Weather', rainy: 'Rainy Weather' };
    return labels[weather] || 'Weather Gear';
  };

  const getWeatherEmoji = (weather) => {
    const emojis = { hot: '☀️', cold: '❄️', rainy: '🌧️' };
    return emojis[weather] || '🌤️';
  };

  const toggleItem = (categoryKey, itemIndex) => {
    setPackingList(prev => {
      const updated = { ...prev };
      updated[categoryKey] = {
        ...updated[categoryKey],
        items: updated[categoryKey].items.map((item, i) => 
          i === itemIndex ? { ...item, checked: !item.checked } : item
        )
      };
      return updated;
    });
    setHasChanges(true);
  };

  const addItem = (categoryKey) => {
    if (!newItemText.trim()) return;
    
    setPackingList(prev => {
      const updated = { ...prev };
      updated[categoryKey] = {
        ...updated[categoryKey],
        items: [...updated[categoryKey].items, { name: newItemText.trim(), checked: false }]
      };
      return updated;
    });
    
    setNewItemText('');
    setAddingToCategory(null);
    setHasChanges(true);
    toast.success('Item added!');
  };

  const removeItem = (categoryKey, itemIndex) => {
    setPackingList(prev => {
      const updated = { ...prev };
      updated[categoryKey] = {
        ...updated[categoryKey],
        items: updated[categoryKey].items.filter((_, i) => i !== itemIndex)
      };
      return updated;
    });
    setHasChanges(true);
  };

  const changeWeather = (weather) => {
    setSelectedWeather(weather);
    setPackingList(prev => ({
      ...prev,
      weather: {
        label: getWeatherLabel(weather),
        emoji: getWeatherEmoji(weather),
        items: [...WEATHER_ITEMS[weather]]
      }
    }));
    setHasChanges(true);
  };

  const savePackingList = async () => {
    if (!trip?.id) {
      toast.error('No trip selected');
      return;
    }
    
    try {
      await offlineStorage.savePackingList(trip.id, {
        categories: packingList,
        weather: selectedWeather,
        tripDestination: trip.destination
      });
      setHasChanges(false);
      toast.success('Packing list saved! 📦');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const resetList = () => {
    generatePackingList();
    toast.success('List reset to defaults');
  };

  // Calculate progress
  const totalItems = Object.values(packingList).reduce((sum, cat) => sum + cat.items.length, 0);
  const checkedItems = Object.values(packingList).reduce(
    (sum, cat) => sum + cat.items.filter(i => i.checked).length, 0
  );
  const progress = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--clay-white)' }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <Briefcase size={32} style={{ color: 'var(--swoopy-pink)' }} />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: 'var(--clay-white)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50 px-4 py-3"
        style={{ 
          backgroundColor: 'rgba(248, 248, 248, 0.92)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
        }}
      >
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <motion.button 
            onClick={onBack} 
            className="flex items-center gap-2 p-2 -ml-2 rounded-xl"
            style={{ color: 'var(--text-secondary)' }}
            whileTap={{ scale: 0.95 }}
            whileHover={{ backgroundColor: 'var(--swoopy-pink-soft)' }}
            data-testid="smart-packing-back-btn"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
            <span className="text-sm font-bold">Back</span>
          </motion.button>
          
          {/* Save Button */}
          <motion.button
            onClick={savePackingList}
            disabled={!hasChanges}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold"
            style={{
              backgroundColor: hasChanges ? 'var(--swoopy-pink)' : 'var(--border-soft)',
              color: hasChanges ? 'white' : 'var(--text-muted)'
            }}
            whileTap={{ scale: 0.95 }}
            data-testid="save-packing-btn"
          >
            <Save size={16} />
            Save
          </motion.button>
        </div>
      </header>

      <motion.div 
        className="max-w-lg mx-auto px-4 pt-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Title */}
        <motion.div variants={itemVariants} className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div 
              className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ 
                background: 'linear-gradient(145deg, var(--swoopy-pink-light), var(--swoopy-pink))',
                boxShadow: 'var(--shadow-clay-btn)'
              }}
            >
              <Briefcase size={24} color="white" />
            </div>
            <div>
              <h1 style={{ 
                fontSize: '24px', 
                fontWeight: '800', 
                color: 'var(--text-primary)',
                fontFamily: "'Fredoka', sans-serif"
              }}>
                Smart Packing
              </h1>
              <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                {trip?.destination ? `Packing for ${trip.destination}` : 'Your travel checklist'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Progress Bar */}
        <motion.div variants={itemVariants} className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>
              {checkedItems} of {totalItems} items packed
            </span>
            <span className="text-sm font-extrabold" style={{ color: 'var(--swoopy-pink)' }}>
              {progress}%
            </span>
          </div>
          <div 
            className="h-3 rounded-full overflow-hidden"
            style={{ backgroundColor: 'var(--border-soft)' }}
          >
            <motion.div
              className="h-full rounded-full"
              style={{ 
                background: 'linear-gradient(90deg, var(--swoopy-pink-light), var(--swoopy-pink))'
              }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, type: 'spring' }}
            />
          </div>
        </motion.div>

        {/* Weather Selector */}
        <motion.div variants={itemVariants} className="mb-6">
          <h2 className="text-sm font-bold mb-3" style={{ color: 'var(--text-secondary)' }}>
            EXPECTED WEATHER
          </h2>
          <div className="flex gap-2">
            {[
              { id: 'hot', icon: Sun, label: 'Hot' },
              { id: 'cold', icon: Snowflake, label: 'Cold' },
              { id: 'rainy', icon: Umbrella, label: 'Rainy' }
            ].map(weather => (
              <motion.button
                key={weather.id}
                onClick={() => changeWeather(weather.id)}
                className="flex-1 py-3 rounded-xl flex flex-col items-center gap-1 relative overflow-hidden"
                style={{ 
                  backgroundColor: selectedWeather === weather.id ? 'var(--swoopy-pink-soft)' : 'var(--card-bg)',
                  color: selectedWeather === weather.id ? 'var(--swoopy-pink)' : 'var(--text-muted)',
                  boxShadow: 'var(--shadow-clay-xs)'
                }}
                whileTap={{ scale: 0.97 }}
                data-testid={`weather-${weather.id}-btn`}
              >
                <weather.icon size={20} />
                <span className="text-xs font-bold">{weather.label}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Packing Categories */}
        <div className="space-y-4">
          {Object.entries(packingList).map(([categoryKey, category]) => (
            <motion.div 
              key={categoryKey}
              variants={itemVariants}
              className="swoopy-card p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{category.emoji}</span>
                  <h3 className="font-bold" style={{ color: 'var(--text-primary)' }}>
                    {category.label}
                  </h3>
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full font-bold"
                    style={{ backgroundColor: 'var(--clay-white)', color: 'var(--text-muted)' }}
                  >
                    {category.items.filter(i => i.checked).length}/{category.items.length}
                  </span>
                </div>
                <motion.button
                  onClick={() => setAddingToCategory(addingToCategory === categoryKey ? null : categoryKey)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ 
                    backgroundColor: 'var(--swoopy-pink-soft)',
                    color: 'var(--swoopy-pink)'
                  }}
                  whileTap={{ scale: 0.9 }}
                  data-testid={`add-item-${categoryKey}-btn`}
                >
                  <Plus size={18} />
                </motion.button>
              </div>

              {/* Add Item Input */}
              <AnimatePresence>
                {addingToCategory === categoryKey && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-3"
                  >
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newItemText}
                        onChange={(e) => setNewItemText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addItem(categoryKey)}
                        placeholder="Add item..."
                        className="flex-1 px-4 py-2 rounded-xl text-sm"
                        style={{ 
                          backgroundColor: 'var(--clay-white)',
                          border: '2px solid var(--border-soft)',
                          color: 'var(--text-primary)'
                        }}
                        autoFocus
                        data-testid={`new-item-input-${categoryKey}`}
                      />
                      <motion.button
                        onClick={() => addItem(categoryKey)}
                        className="px-4 py-2 rounded-xl text-sm font-bold"
                        style={{ 
                          backgroundColor: 'var(--swoopy-pink)',
                          color: 'white'
                        }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Add
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Items List */}
              <div className="space-y-2">
                {category.items.map((item, index) => (
                  <motion.div
                    key={`${categoryKey}-${index}`}
                    className="flex items-center gap-3 p-3 rounded-xl group"
                    style={{ backgroundColor: 'var(--clay-white)' }}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.02 }}
                  >
                    <motion.button
                      onClick={() => toggleItem(categoryKey, index)}
                      className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ 
                        backgroundColor: item.checked ? 'var(--swoopy-pink)' : 'var(--card-bg)',
                        border: item.checked ? 'none' : '2px solid var(--border-soft)'
                      }}
                      whileTap={{ scale: 0.9 }}
                      data-testid={`toggle-item-${categoryKey}-${index}`}
                    >
                      {item.checked && <Check size={14} color="white" strokeWidth={3} />}
                    </motion.button>
                    
                    <span 
                      className="flex-1 text-sm font-medium"
                      style={{ 
                        color: item.checked ? 'var(--text-muted)' : 'var(--text-primary)',
                        textDecoration: item.checked ? 'line-through' : 'none'
                      }}
                    >
                      {item.name}
                    </span>
                    
                    <motion.button
                      onClick={() => removeItem(categoryKey, index)}
                      className="w-6 h-6 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ color: 'var(--text-muted)' }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Trash2 size={14} />
                    </motion.button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Reset Button */}
        <motion.div variants={itemVariants} className="mt-6">
          <motion.button
            onClick={resetList}
            className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
            style={{ 
              backgroundColor: 'transparent',
              color: 'var(--text-muted)',
              border: '2px dashed var(--border-soft)'
            }}
            whileTap={{ scale: 0.98 }}
            data-testid="reset-packing-btn"
          >
            <RotateCcw size={16} />
            Reset to Defaults
          </motion.button>
        </motion.div>

        {/* Tip */}
        <motion.div 
          variants={itemVariants}
          className="mt-6 p-4 rounded-2xl text-center"
          style={{ backgroundColor: 'var(--swoopy-pink-soft)' }}
        >
          <div className="flex items-center justify-center gap-2 mb-1">
            <Sparkles size={16} style={{ color: 'var(--swoopy-pink)' }} />
            <span className="text-sm font-bold" style={{ color: 'var(--swoopy-pink)' }}>Pro Tip</span>
          </div>
          <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
            Pack half of what you think you need. You can always buy essentials at your destination!
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
